import ChargingPeriodAccordionItem from './ChargingPeriodAccordionItem'
import Styles from './ChargingPeriodAccordion.module.css'
import Colors from 'Theme/Colors'
import { IFText } from 'Components'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import styled from 'styled-components'
import InfinityEnums from 'Enums/InfinityEnums'

const StyledDiv = styled.div`
  &:hover {
    color: whitesmoke;
  }
`

const getDimensionVolume = (p, type) => {
  const dim = p?.dimensions?.find((d) => d.type === type)
  if (typeof dim?.volume === 'number') {
    return dim.volume.toFixed(3)
  }
  return ''
}

export const normalizePeriod = (p) => ({
  id: crypto?.randomUUID?.() ?? String(Date.now() + Math.random()),
  startTime: p?.startDateTime ? new Date(p.startDateTime) : null,
  time: getDimensionVolume(p, InfinityEnums.CdrDimensionType.TIME),
  energy: getDimensionVolume(p, InfinityEnums.CdrDimensionType.ENERGY),
  power: getDimensionVolume(p, InfinityEnums.CdrDimensionType.POWER),
  current: getDimensionVolume(p, InfinityEnums.CdrDimensionType.CURRENT),
  stateOfCharge: getDimensionVolume(
    p,
    InfinityEnums.CdrDimensionType.STATE_OF_CHARGE,
  ),
  parkingTime: getDimensionVolume(
    p,
    InfinityEnums.CdrDimensionType.PARKING_TIME,
  ),
})

export const makeEmptyPeriod = () => ({
  id: crypto?.randomUUID?.() ?? String(Date.now() + Math.random()),
  startTime: null,
  time: '',
  energy: '',
  power: '',
  current: '',
  stateOfCharge: '',
  parkingTime: '',
})

const ChargingPeriodAccordion = ({ periods = [], onChange, onErrorChange }) => {
  const { t } = useTranslation()
  const buttonRef = useRef(null)
  const [items, setItems] = useState([{ ...makeEmptyPeriod(), removed: false }])
  const [hasAnyError, setHasAnyError] = useState(false)

  useEffect(() => {
    onErrorChange?.(hasAnyError)
  }, [hasAnyError, onErrorChange])

  useEffect(() => {
    if (Array.isArray(periods) && periods.length > 0) {
      setItems(
        periods.map((p) => ({
          ...normalizePeriod(p),
          removed: false,
        })),
      )
    } else {
      setItems([{ ...makeEmptyPeriod(), removed: false }])
    }
  }, [])

  useEffect(() => {
    if (!onChange) return
    onChange(
      items
        .filter((i) => !i.removed)
        .map(
          ({
            startTime,
            time,
            stateOfCharge,
            energy,
            current,
            power,
            parkingTime,
          }) => ({
            startTime,
            time,
            stateOfCharge,
            energy,
            current,
            power,
            parkingTime,
          }),
        ),
    )
  }, [items])

  const addPeriod = () => {
    setItems((prev) => [...prev, { ...makeEmptyPeriod(), removed: false }])
    buttonRef.current?.focus()
  }

  const togglePeriodRemoved = (idToToggle) => {
    setItems((prev) =>
      prev.map((p) =>
        p.id === idToToggle ? { ...p, removed: !p.removed } : p,
      ),
    )
  }

  const handleItemChange = (id, updated) => {
    setItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...updated } : p)),
    )
  }

  const handleItemErrorChange = (id, hasError) => {
    if (hasError) {
      setHasAnyError(true)
      return
    }
    setHasAnyError(false)
  }

  return (
    <div className={Styles.ContainerDiv}>
      <div className={Styles.ContentContainer}>
        {items.map((item, idx) => (
          <ChargingPeriodAccordionItem
            key={item.id}
            id={item.id}
            index={idx}
            value={item}
            showRemove={items.length > 1}
            removed={item.removed}
            onChange={(payload) => handleItemChange(item.id, payload)}
            onErrorChange={(err) => handleItemErrorChange(item.id, err)}
            onToggleRemove={() => togglePeriodRemoved(item.id)}
          />
        ))}
      </div>

      <div className={Styles.AddButtonDiv}>
        <button
          type="button"
          className={Styles.AddButton}
          style={{ color: Colors.primary }}
          onClick={addPeriod}
          ref={buttonRef}
        >
          <StyledDiv
            className={Styles.IconContainer}
            style={{ color: Colors.primary }}
          >
            <IFText className={Styles.AddButtonText}>
              {t('TariffModal.AddElement')}
            </IFText>
          </StyledDiv>
        </button>
      </div>
    </div>
  )
}
ChargingPeriodAccordion.propTypes = {
  periods: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
  onErrorChange: PropTypes.func,
}

export default ChargingPeriodAccordion
